/**
 * Ordinal color palette for organizations.
 *
 * Hues are spaced around the wheel at L≈0.70 / C≈0.13 so every color has the
 * same perceptual weight — no org reads as "louder" than another. Foreground
 * colors are chosen per-entry: light foreground on most, darker foreground on
 * the amber tile where the high luminance + yellow band tanks contrast of
 * light text.
 *
 * Colors are assigned by the org's ordinal position in the user's org list
 * (`index % palette.length`). Ordinal wins over hashing by id because it
 * gives the "my first org is the same color for everyone" reassurance and
 * stays crisp for the 1–3-org case most users hit.
 */

export interface OrgColor {
    bg: string;
    fg: string;
    /** Human-readable name for the hue, handy for debugging / storybook. */
    name: string;
}

export const ORG_PALETTE: readonly OrgColor[] = [
    { name: "blue", bg: "oklch(0.70 0.12 250)", fg: "oklch(0.98 0.02 250)" },
    { name: "teal", bg: "oklch(0.72 0.13 160)", fg: "oklch(0.98 0.02 160)" },
    { name: "amber", bg: "oklch(0.78 0.14 80)", fg: "oklch(0.22 0.06 80)" },
    { name: "terracotta", bg: "oklch(0.70 0.14 30)", fg: "oklch(0.98 0.02 30)" },
    { name: "magenta", bg: "oklch(0.68 0.13 320)", fg: "oklch(0.98 0.02 320)" },
    { name: "green", bg: "oklch(0.72 0.13 140)", fg: "oklch(0.98 0.02 140)" },
] as const;

export function getOrgColor(index: number): OrgColor {
    const safeIndex = index < 0 ? 0 : index % ORG_PALETTE.length;
    // biome-ignore lint/style/noNonNullAssertion: ORG_PALETTE is a non-empty compile-time constant; safeIndex is bounded by its length. Shrinking the palette to zero would require a source-level change that would also need this function rewritten.
    return ORG_PALETTE[safeIndex]!;
}
